import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bell, Check, Trash2, Calendar, AlertTriangle, Clock, Plus, ExternalLink, RotateCcw } from "lucide-react";
import { fmtDate } from "@/lib/format";

const SCOPES = [
  { key: "mine", label: "Miei aperti", icon: Bell },
  { key: "today", label: "Oggi", icon: Calendar },
  { key: "overdue", label: "In ritardo", icon: AlertTriangle },
  { key: "upcoming", label: "Futuri", icon: Clock },
  { key: "all", label: "Tutti aperti", icon: Bell },
  { key: "done", label: "Completati", icon: Check },
];

const daysDiff = (dueStr) => {
  if (!dueStr) return null;
  const due = new Date(dueStr + "T00:00:00Z");
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return Math.round((due - today) / (24 * 3600 * 1000));
};

const dueBadge = (dueStr, done) => {
  if (done) return { text: "Completato", cls: "bg-green-50 text-green-700 border-green-300" };
  const d = daysDiff(dueStr);
  if (d === null) return { text: "—", cls: "bg-slate-50 border-slate-300 text-slate-500" };
  if (d < 0) return { text: `In ritardo di ${Math.abs(d)}g`, cls: "bg-red-50 text-red-700 border-red-300" };
  if (d === 0) return { text: "Oggi", cls: "bg-orange-50 text-orange-700 border-orange-300" };
  if (d <= 3) return { text: `Tra ${d}g`, cls: "bg-yellow-50 text-yellow-800 border-yellow-300" };
  return { text: `Tra ${d}g`, cls: "bg-slate-50 text-slate-700 border-slate-300" };
};

export default function FollowUpsPage() {
  const { user } = useAuth();
  const [scope, setScope] = useState("mine");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ offer_id: "", due_date: "", assigned_to: "", note: "" });

  const load = async (s = scope) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/followups?scope=${s}`);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(scope); }, [scope]);
  useEffect(() => {
    api.get("/offers").then(({ data }) => setOffers(data)).catch(() => {});
    if (user?.role === "admin" || user?.role === "direzione") {
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [user]);

  const openNew = () => {
    const due = new Date();
    due.setDate(due.getDate() + 7);
    setForm({
      offer_id: "",
      due_date: due.toISOString().slice(0, 10),
      assigned_to: user?.id || "",
      note: "",
    });
    setNewOpen(true);
  };

  const create = async () => {
    if (!form.offer_id) return toast.error("Seleziona un'offerta");
    if (!form.due_date) return toast.error("Seleziona una data");
    try {
      await api.post("/followups", form);
      toast.success("Follow-up creato");
      setNewOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const toggleDone = async (fu) => {
    try {
      await api.patch(`/followups/${fu.id}`, { done: !fu.done });
      toast.success(fu.done ? "Riaperto" : "Completato");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const del = async (fu) => {
    if (!window.confirm("Eliminare questo follow-up?")) return;
    try {
      await api.delete(`/followups/${fu.id}`);
      toast.success("Eliminato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const scan = async () => {
    try {
      const { data } = await api.post("/followups/scan-due");
      toast.success(`Scansione completata: ${data.notifications_sent} notifiche inviate`);
    } catch (e) { toast.error("Errore scansione"); }
  };

  const counts = useMemo(() => {
    const by = { overdue: 0, today: 0, upcoming: 0 };
    for (const r of rows) {
      if (r.done) continue;
      const d = daysDiff(r.due_date);
      if (d === null) continue;
      if (d < 0) by.overdue++;
      else if (d === 0) by.today++;
      else by.upcoming++;
    }
    return by;
  }, [rows]);

  const canScan = user?.role === "admin" || user?.role === "direzione";

  return (
    <div className="space-y-6" data-testid="followups-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Commerciale</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0F2C59]">Follow-up</h1>
          <p className="text-slate-500 text-sm md:text-base">Promemoria di richiamo cliente sulle offerte inviate.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canScan && (
            <Button variant="outline" onClick={scan} data-testid="fu-scan-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Scansiona scaduti
            </Button>
          )}
          <Button onClick={openNew} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="fu-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo follow-up
          </Button>
        </div>
      </div>

      {/* KPI mini bar */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <Card className={`p-4 border ${counts.overdue > 0 ? "border-red-300 bg-red-50/60" : "border-slate-200"}`} data-testid="fu-kpi-overdue">
          <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 font-mono">In ritardo</div>
          <div className="mt-1 flex items-center gap-2">
            <AlertTriangle className={`w-6 h-6 p-1 rounded-md ${counts.overdue > 0 ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-400"}`} />
            <div className={`text-2xl md:text-3xl font-display font-bold ${counts.overdue > 0 ? "text-red-600" : "text-slate-500"}`}>{counts.overdue}</div>
          </div>
        </Card>
        <Card className="p-4 border-slate-200" data-testid="fu-kpi-today">
          <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 font-mono">Oggi</div>
          <div className="mt-1 flex items-center gap-2">
            <Calendar className="w-6 h-6 p-1 rounded-md bg-orange-100 text-orange-700" />
            <div className="text-2xl md:text-3xl font-display font-bold text-orange-700">{counts.today}</div>
          </div>
        </Card>
        <Card className="p-4 border-slate-200" data-testid="fu-kpi-upcoming">
          <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 font-mono">Futuri</div>
          <div className="mt-1 flex items-center gap-2">
            <Clock className="w-6 h-6 p-1 rounded-md bg-slate-100 text-[#0F2C59]" />
            <div className="text-2xl md:text-3xl font-display font-bold text-[#0F2C59]">{counts.upcoming}</div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 -mb-px">
        {SCOPES.map((s) => {
          const Icon = s.icon;
          const active = scope === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${active ? "border-[#0F2C59] text-[#0F2C59]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`fu-tab-${s.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Bell className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            Nessun follow-up.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((fu) => {
              const badge = dueBadge(fu.due_date, fu.done);
              return (
                <li key={fu.id} className="p-4 hover:bg-slate-50" data-testid={`fu-row-${fu.id}`}>
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleDone(fu)}
                      className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 ${fu.done ? "bg-green-600 border-green-600 text-white" : "border-slate-300 hover:border-[#0F2C59]"}`}
                      data-testid={`fu-toggle-${fu.id}`}
                      title={fu.done ? "Riapri" : "Segna come completato"}
                    >
                      {fu.done && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`/offerte/${fu.offer_id}`}
                          className="font-mono font-semibold text-[#0F2C59] hover:underline flex items-center gap-1"
                        >
                          {fu.offer_numero || "—"} <ExternalLink className="w-3 h-3" />
                        </Link>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-700">{fu.cliente_nome || "—"}</span>
                        <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-full border ${badge.cls}`}>
                          {badge.text}
                        </span>
                      </div>
                      {fu.note && <div className={`text-sm mt-1 ${fu.done ? "line-through text-slate-400" : "text-slate-600"}`}>{fu.note}</div>}
                      <div className="text-xs text-slate-400 mt-1 font-mono">
                        Scadenza {fmtDate(fu.due_date)}
                        {fu.assigned_to_name && <> · Assegnato a {fu.assigned_to_name}</>}
                        {fu.done_at && <> · Chiuso {fmtDate(fu.done_at)}</>}
                      </div>
                    </div>
                    <button onClick={() => del(fu)} className="p-1.5 hover:bg-red-50 text-red-500 rounded shrink-0" data-testid={`fu-delete-${fu.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* New follow-up dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo follow-up</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Offerta *</Label>
              <Select value={form.offer_id} onValueChange={(v) => setForm({ ...form, offer_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="fu-form-offer"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>
                  {offers.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.numero} — {o.cliente?.ragione_sociale || "—"} [{o.stato}]
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Data scadenza *</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" data-testid="fu-form-date" /></div>
            {(user?.role === "admin" || user?.role === "direzione") && (
              <div>
                <Label>Assegnato a</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="fu-form-user"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.active).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Nota</Label><Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" placeholder="Es. Richiamare per verificare interesse…" data-testid="fu-form-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button>
            <Button onClick={create} className="bg-[#0F2C59]" data-testid="fu-form-save">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
